import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PublicOrganizationProfile } from '@/services/publicOrg';

export type OrgRegisterMode = 'club' | 'academy';

export type OpenOrgRegisterOptions = {
  mode?: OrgRegisterMode;
};

type OrgRegisterDialogContextValue = {
  openOrgRegister: (returnUrl?: string, options?: OpenOrgRegisterOptions) => void;
  closeOrgRegister: () => void;
  dialog: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    returnUrl: string;
    mode: OrgRegisterMode;
  };
};

const OrgRegisterDialogContext = createContext<OrgRegisterDialogContextValue | null>(null);

interface OrgRegisterDialogProviderProps {
  profile: PublicOrganizationProfile;
  defaultReturnUrl: string;
  children: React.ReactNode;
}

export function OrgRegisterDialogProvider({
  profile: _profile,
  defaultReturnUrl,
  children,
}: OrgRegisterDialogProviderProps) {
  const [open, setOpen] = useState(false);
  const [returnUrl, setReturnUrl] = useState(defaultReturnUrl);
  const [mode, setMode] = useState<OrgRegisterMode>('club');

  const openOrgRegister = useCallback(
    (returnUrlOverride?: string, options?: OpenOrgRegisterOptions) => {
      setReturnUrl(returnUrlOverride ?? defaultReturnUrl);
      setMode(options?.mode ?? 'club');
      setOpen(true);
    },
    [defaultReturnUrl]
  );

  const closeOrgRegister = useCallback(() => {
    setOpen(false);
    setReturnUrl(defaultReturnUrl);
    setMode('club');
  }, [defaultReturnUrl]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        setReturnUrl(defaultReturnUrl);
        setMode('club');
      }
    },
    [defaultReturnUrl]
  );

  const dialog = useMemo(
    () => ({
      open,
      onOpenChange: handleOpenChange,
      returnUrl,
      mode,
    }),
    [handleOpenChange, mode, open, returnUrl]
  );

  const value = useMemo(
    () => ({ openOrgRegister, closeOrgRegister, dialog }),
    [closeOrgRegister, dialog, openOrgRegister]
  );

  return (
    <OrgRegisterDialogContext.Provider value={value}>{children}</OrgRegisterDialogContext.Provider>
  );
}

export function useOrgRegisterDialog(): OrgRegisterDialogContextValue {
  const ctx = useContext(OrgRegisterDialogContext);
  if (!ctx) {
    throw new Error('useOrgRegisterDialog must be used within OrgRegisterDialogProvider');
  }
  return ctx;
}

export function useOrgRegisterDialogOptional(): OrgRegisterDialogContextValue | null {
  return useContext(OrgRegisterDialogContext);
}
